import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { TrainerMemberService } from '../../../lib/trainerMemberService';
import { WorkoutService } from '../../../lib/workoutService';
import { TrainerMemberRelationshipWithMember } from '../../../types/dataModel';
import {
  WorkoutPlanWithSchedule,
  MemberWorkoutPlan,
  WorkoutSession,
  WorkoutWithBlocks,
  WorkoutSummary,
} from '../../../types/dataModel';
import { useAuth } from '../../../contexts/AuthContext';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { TrainerAppLayout } from '../shell/TrainerAppLayout';
import {
  createTrainerDrillDownSectionSelect,
  persistTrainerDashboardSection,
} from '../shell/trainerSectionNav';
import {
  Calendar,
  Check,
  Plus,
  Search,
  X,
  Copy,
  Trash2,
} from '../bravosIcons';
import { useCachedData } from '../../../hooks/useCachedData';
import CacheService from '../../../lib/cacheService';
import { toLocalDateKey } from '../../../utils/dateKey';
import { PAGE_CHROME_LOADER_BRAND_GREEN } from '../../../constants/pageChrome';
import { usePageStatusBar } from '../../../hooks/usePageStatusBar';
import { displayNameFromProfile, getInitials } from '../../../pages/account/accountShared';
import { messageFromUnknownError } from '../../../utils/unknownErrorMessage';
import { paddedWideRangeForMemberCardSessions } from '../../../lib/memberLast7DaysActivity';
import type { SessionLike } from '../../../lib/memberLast7DaysActivity';
import {
  CADENCE_TOOLTIP,
  computeMemberRosterMetrics,
  formatCadenceDisplay,
  joinDateYmd,
  type MemberRosterMetrics,
} from '../../../lib/memberCadenceMetrics';
import {
  designBadgeLabel,
  mapSessionStatusToDesignBadge,
} from './memberDetail/sessionStatusBadge';
import {
  buildMemberCalendarGrid,
  exerciseCountFromWorkout,
  formatSessionCardMeta,
  formatSessionCardMetaCompact,
  formatShortCalendarDate,
  memberCalendarVisibleRange,
} from './memberDetail/calendarInteractions';
import { TrainerWorkoutLibraryDrawer } from '../overlays/TrainerWorkoutLibraryDrawer';
import './MemberDetail.css';

type MemberWorkoutPlanWithDetails = MemberWorkoutPlan & { workoutPlan: WorkoutPlanWithSchedule };

/** Session with optional workout — accepts light ({ id, name, estimatedDurationMinutes }) or full. */
type CalendarSession = Omit<WorkoutSession, 'workout'> & {
  workout?: { id: string; name: string; estimatedDurationMinutes?: number } | WorkoutWithBlocks;
};

type MemberDetailCachePayload = {
  relationship: TrainerMemberRelationshipWithMember | null;
  assignedPlans: MemberWorkoutPlanWithDetails[];
  calendarMonth: { year: number; month: number };
  sessions: CalendarSession[];
};

type PlanFilterValue = null | string | '__individual__';

type StampState = {
  workoutId: string;
  name: string;
  sourceYmd: string;
  pasteCount: number;
  pendingDates: string[];
  estimatedDurationMinutes?: number;
  exerciseCount?: number | null;
  saving?: boolean;
};

type ToastState = {
  title: string;
  sub: string;
  undo?: () => void;
};

type UndoSnapshot = {
  workoutId: string;
  scheduledDate: string;
  name: string;
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MAX_VISIBLE_CARDS = 1;
const UNDO_TOAST_MS = 5000;
const DEFAULT_TOAST_MS = 3200;

const STRIP_TITLE =
  "8 bars = last 8 calendar weeks. Bar height = that week's sessions as a % of personal weekly target.";

function emptyMetrics(): MemberRosterMetrics {
  return {
    strip: Array.from({ length: 8 }, () => ({
      weekStart: '',
      completed: 0,
      planned: 0,
      bar: 'miss' as const,
    })),
    cadence: 0,
    last: { label: '—', warn: false, daysAgo: null },
    next: { label: 'Not scheduled', kind: 'empty', nextYmd: null },
    trainingToday: false,
    needsAttention: false,
    needsAttentionTooltip: null,
  };
}

function openNativeDatePicker(input: HTMLInputElement) {
  if ('showPicker' in input && typeof input.showPicker === 'function') {
    try {
      input.showPicker();
      return;
    } catch {
      // showPicker may throw if not triggered by user gesture in some browsers
    }
  }
  input.focus();
}

/** Estimate sessions/wk from plan schedule density for assign modal subtitle. */
function estimateSessionsPerWeek(plan: WorkoutPlanWithSchedule): number | null {
  const workouts = plan.planWorkouts?.length ?? 0;
  const days = plan.totalNoOfDays;
  if (!workouts || !days) return null;
  return Math.max(1, Math.round((workouts / days) * 7));
}

function formatDateLong(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function sessionDateKey(s: CalendarSession): string {
  return (s.scheduledDate || '').split('T')[0];
}

function sessionDurationMin(s: CalendarSession): number {
  if (typeof s.actualDurationMinutes === 'number' && s.actualDurationMinutes > 0) {
    return s.actualDurationMinutes;
  }
  const est = s.workout && 'estimatedDurationMinutes' in s.workout ? s.workout.estimatedDurationMinutes : undefined;
  return typeof est === 'number' ? est : 0;
}

function sessionTitle(s: CalendarSession): string {
  return s.workout?.name || 'Workout';
}

function sessionWorkoutId(s: CalendarSession): string | null {
  return s.workoutId || s.workout?.id || null;
}

function isStampPendingSessionId(id: string): boolean {
  return String(id).startsWith('stamp-pending-');
}

function mergeSessionsPreservingStampPending(
  incoming: CalendarSession[],
  previous: CalendarSession[]
): CalendarSession[] {
  const pending = previous.filter((s) => isStampPendingSessionId(s.id));
  if (pending.length === 0) return incoming;
  return [...incoming, ...pending];
}

export const MemberDetailPage = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const trainerId = profile?.id ?? '';

  const [relationship, setRelationship] = useState<TrainerMemberRelationshipWithMember | null>(null);
  const [assignedPlans, setAssignedPlans] = useState<MemberWorkoutPlanWithDetails[]>([]);
  const [allWorkoutSessions, setAllWorkoutSessions] = useState<CalendarSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const sessionsLoadedForMonthRef = useRef<{ year: number; month: number } | null>(null);
  const calWeeksRef = useRef<HTMLDivElement | null>(null);
  const profileMainRef = useRef<HTMLElement | null>(null);
  /** After show more/less / Today, scroll this day key to the top of the profile pane. */
  const scrollDayIntoViewKeyRef = useRef<string | null>(null);
  /** Keep the pin across the sessions fetch when Today switches months (heights change after load). */
  const scrollDayAwaitSessionsRef = useRef(false);
  /** Set when month sessions arrive so the next equalize layout can re-pin then clear. */
  const scrollDayReleaseAfterLayoutRef = useRef(false);

  const scrollDayToTopOfMain = useCallback((dayKey: string) => {
    const main = profileMainRef.current;
    const dayEl = calWeeksRef.current?.querySelector(
      `[data-day-key="${dayKey}"]`
    ) as HTMLElement | null;
    if (!main || !dayEl) return;
    const mainRect = main.getBoundingClientRect();
    const dayRect = dayEl.getBoundingClientRect();
    main.scrollTop = Math.max(0, main.scrollTop + (dayRect.top - mainRect.top));
  }, []);

  const finishPendingDayScroll = useCallback(() => {
    const key = scrollDayIntoViewKeyRef.current;
    if (key) scrollDayToTopOfMain(key);
    scrollDayIntoViewKeyRef.current = null;
    scrollDayAwaitSessionsRef.current = false;
    scrollDayReleaseAfterLayoutRef.current = false;
  }, [scrollDayToTopOfMain]);

  const [planFilter, setPlanFilter] = useState<PlanFilterValue>(null);
  const [planFilterOpen, setPlanFilterOpen] = useState(false);
  const [planFilterPos, setPlanFilterPos] = useState<{ left: number; top: number } | null>(null);
  const planFilterBtnRef = useRef<HTMLButtonElement | null>(null);

  const [visibleCardLimit, setVisibleCardLimit] = useState(MAX_VISIBLE_CARDS);

  const [drawerSession, setDrawerSession] = useState<CalendarSession | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<WorkoutPlanWithSchedule[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [assignPlanDate, setAssignPlanDate] = useState<string>(() => toLocalDateKey(new Date()));
  const [assignPlanSearch, setAssignPlanSearch] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const assignPlanDateRef = useRef<HTMLInputElement | null>(null);

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryDate, setLibraryDate] = useState<string | null>(null);
  const [libraryWorkouts, setLibraryWorkouts] = useState<WorkoutSummary[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryAssigningId, setLibraryAssigningId] = useState<string | null>(null);

  const [stamp, setStamp] = useState<StampState | null>(null);
  const stampRef = useRef<StampState | null>(null);
  stampRef.current = stamp;

  const [toast, setToast] = useState<ToastState | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const undoSnapshotRef = useRef<UndoSnapshot | null>(null);

  const [selectedMetrics, setSelectedMetrics] = useState<MemberRosterMetrics>(emptyMetrics);
  const metricsGenRef = useRef(0);
  const todayYmd = toLocalDateKey(new Date());

  const TRAINER_WORKOUTS_CACHE_KEY = `trainer_workouts_${profile?.id ?? ''}`;
  const TRAINER_WORKOUTS_TTL_MS = 2 * 60 * 1000;

  usePageStatusBar({
    background: loading ? PAGE_CHROME_LOADER_BRAND_GREEN : 'var(--bg-page)',
  });

  const showToast = useCallback((title: string, sub: string, undo?: () => void, ms?: number) => {
    setToast({ title, sub, undo });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      undoSnapshotRef.current = null;
    }, ms ?? (undo ? UNDO_TOAST_MS : DEFAULT_TOAST_MS));
  }, []);

  const workoutMetaById = useMemo(() => {
    const map: Record<string, { exerciseCount: number; duration?: number }> = {};
    libraryWorkouts.forEach((w) => {
      map[w.id] = {
        exerciseCount: w.exerciseCount,
        duration: w.estimatedDurationMinutes,
      };
    });
    return map;
  }, [libraryWorkouts]);

  const sessionExerciseCount = useCallback(
    (s: CalendarSession): number | null => {
      const fromWorkout = exerciseCountFromWorkout(s.workout);
      if (fromWorkout != null) return fromWorkout;
      const wid = sessionWorkoutId(s);
      if (wid && workoutMetaById[wid]) return workoutMetaById[wid].exerciseCount;
      return null;
    },
    [workoutMetaById]
  );

  const preloadedRelationshipRef = useRef<TrainerMemberRelationshipWithMember | null>(null);
  const currentCalendarMonthRef = useRef(currentCalendarMonth);
  currentCalendarMonthRef.current = currentCalendarMonth;
  preloadedRelationshipRef.current =
    (location.state as { memberRelationship?: TrainerMemberRelationshipWithMember } | null)?.memberRelationship ??
    null;

  const fetchMemberDetailData = useCallback(async (): Promise<MemberDetailCachePayload> => {
    if (!memberId || !profile?.id) {
      return {
        relationship: null,
        assignedPlans: [],
        calendarMonth: currentCalendarMonthRef.current,
        sessions: [],
      };
    }
    const preloaded = preloadedRelationshipRef.current;
    const usePreloaded =
      preloaded &&
      preloaded.memberId === memberId &&
      preloaded.status !== 'pending' &&
      preloaded.status !== 'rejected';
    const { year, month } = currentCalendarMonthRef.current;
    const { fromDate, toDate } = memberCalendarVisibleRange(year, month);

    let rel: TrainerMemberRelationshipWithMember | null;
    if (usePreloaded) {
      rel = preloaded;
    } else {
      const fetched = await TrainerMemberService.getTrainerMemberByMemberId(profile.id, memberId);
      if (!fetched) throw new Error('Member not found');
      if (fetched.status === 'pending') {
        throw new Error(
          'This member has not yet accepted your connection request. Please wait for them to accept before accessing their details.'
        );
      }
      rel = fetched;
    }

    const [plans, monthSessions] = await Promise.all([
      WorkoutService.getMemberWorkoutPlansWithLightDetails(memberId, profile.id),
      WorkoutService.getMemberWorkoutSessionsLight(memberId, fromDate, toDate, profile.id),
    ]);
    return {
      relationship: rel,
      assignedPlans: plans,
      calendarMonth: { year, month },
      sessions: monthSessions,
    };
  }, [memberId, profile?.id]);

  const MEMBER_DETAIL_CACHE_KEY = `member_detail_v3_${memberId ?? ''}`;
  const {
    data: memberDetailData,
    loading: memberDetailLoading,
    error: memberDetailError,
    refetch: refetchMemberDetail,
    refetchInBackground,
    invalidate: invalidateMemberDetailCache,
  } = useCachedData<MemberDetailCachePayload>({
    cacheKey: MEMBER_DETAIL_CACHE_KEY,
    fetchFn: fetchMemberDetailData,
    ttl: 2 * 60 * 1000,
    staleWhileRevalidate: true,
    enabled: !!memberId && !!profile?.id,
  });

  useEffect(() => {
    if (!memberDetailData) return;
    setRelationship(memberDetailData.relationship ?? null);
    setAssignedPlans(memberDetailData.assignedPlans);
    setCurrentCalendarMonth(memberDetailData.calendarMonth);
    sessionsLoadedForMonthRef.current = memberDetailData.calendarMonth;
    setAllWorkoutSessions((prev) =>
      mergeSessionsPreservingStampPending(memberDetailData.sessions, prev)
    );
    setError(null);
    setPlanFilter(null);
  }, [memberDetailData]);

  useEffect(() => {
    setLoading(memberDetailLoading);
  }, [memberDetailLoading]);

  useEffect(() => {
    if (memberDetailError) setError(messageFromUnknownError(memberDetailError));
  }, [memberDetailError]);

  const didRefetchInBackgroundRef = useRef<string | null>(null);
  useEffect(() => {
    if (!memberId || !memberDetailData) return;
    if (didRefetchInBackgroundRef.current === memberId) return;
    didRefetchInBackgroundRef.current = memberId;
    refetchInBackground();
  }, [memberId, memberDetailData, refetchInBackground]);

  useEffect(() => {
    const state = location.state as { refreshMember?: boolean } | null;
    if (state?.refreshMember && memberId && profile?.id) {
      invalidateMemberDetailCache();
      refetchMemberDetail();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [
    location.state,
    location.pathname,
    memberId,
    profile?.id,
    navigate,
    invalidateMemberDetailCache,
    refetchMemberDetail,
  ]);

  useEffect(() => {
    if ((location.state as { scrollToCalendar?: boolean })?.scrollToCalendar && !loading) {
      const el = document.getElementById('workout-calendar');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, location.state]);

  // Prefetch trainer workout summaries for card meta + library
  useEffect(() => {
    if (!profile?.id) return;
    const cached = CacheService.get<WorkoutSummary[]>(TRAINER_WORKOUTS_CACHE_KEY);
    if (cached) setLibraryWorkouts(cached);
    let cancelled = false;
    WorkoutService.getWorkoutsSummary()
      .then((workouts) => {
        if (cancelled) return;
        const trainerWorkouts = workouts.filter((w) => w.createdBy === profile.id);
        setLibraryWorkouts(trainerWorkouts);
        CacheService.set(TRAINER_WORKOUTS_CACHE_KEY, trainerWorkouts, { ttl: TRAINER_WORKOUTS_TTL_MS });
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [profile?.id, TRAINER_WORKOUTS_CACHE_KEY]);

  const refreshPlansAndSessionsForVisibleMonth = useCallback(
    async (monthOverride?: { year: number; month: number }) => {
      const actualMemberId = relationship?.member?.id ?? relationship?.memberId ?? memberId;
      if (!actualMemberId || !profile?.id) return;
      const { year, month } = monthOverride ?? currentCalendarMonth;
      const { fromDate, toDate } = memberCalendarVisibleRange(year, month);
      const [plans, sessions] = await Promise.all([
        WorkoutService.getMemberWorkoutPlansWithLightDetails(actualMemberId, profile.id),
        WorkoutService.getMemberWorkoutSessionsLight(actualMemberId, fromDate, toDate, profile.id),
      ]);
    sessionsLoadedForMonthRef.current = { year, month };
    setAssignedPlans(plans);
    setAllWorkoutSessions((prev) => mergeSessionsPreservingStampPending(sessions, prev));
    if (monthOverride) setCurrentCalendarMonth(monthOverride);
    CacheService.invalidate(MEMBER_DETAIL_CACHE_KEY);
    },
    [
      relationship?.member?.id,
      relationship?.memberId,
      memberId,
      profile?.id,
      currentCalendarMonth,
      MEMBER_DETAIL_CACHE_KEY,
    ]
  );

  // Create-workout return → auto-assign (once per payload)
  const assignCreatedHandledRef = useRef<string | null>(null);
  useEffect(() => {
    const state = location.state as {
      assignCreatedWorkout?: { workoutId: string; date: string; name?: string };
      scrollToCalendar?: boolean;
    } | null;
    const payload = state?.assignCreatedWorkout;
    if (!payload?.workoutId || !payload.date || !memberId || loading) return;
    const dedupeKey = `${payload.workoutId}:${payload.date}`;
    if (assignCreatedHandledRef.current === dedupeKey) return;
    assignCreatedHandledRef.current = dedupeKey;

    let cancelled = false;
    (async () => {
      try {
        const actualMemberId = relationship?.member?.id || relationship?.memberId || memberId;
        await WorkoutService.assignWorkoutToMember(actualMemberId, payload.workoutId, payload.date);
        if (cancelled) return;
        const [y, m] = payload.date.split('-').map(Number);
        await refreshPlansAndSessionsForVisibleMonth(
          y && m ? { year: y, month: m - 1 } : undefined
        );
        showToast('Workout added', `${payload.name || 'Workout'} is on their calendar.`);
      } catch (err: unknown) {
        if (!cancelled) {
          showToast('Could not assign workout', messageFromUnknownError(err) || 'Try again from the calendar.');
        }
      } finally {
        if (!cancelled) {
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    location.state,
    location.pathname,
    memberId,
    loading,
    relationship?.member?.id,
    relationship?.memberId,
    navigate,
    refreshPlansAndSessionsForVisibleMonth,
    showToast,
  ]);

  // Reset overlays when switching member via URL
  useEffect(() => {
    setVisibleCardLimit(MAX_VISIBLE_CARDS);
    setDrawerOpen(false);
    setDrawerSession(null);
    setAssignOpen(false);
    setPlanFilterOpen(false);
    setLibraryOpen(false);
    setLibraryDate(null);
    setStamp(null);
  }, [memberId]);

  // Metrics for selected member only
  useEffect(() => {
    if (!trainerId || !memberId || !relationship) {
      metricsGenRef.current += 1;
      setSelectedMetrics(emptyMetrics());
      return;
    }
    const loadGen = ++metricsGenRef.current;
    const { fromStr, toStr } = paddedWideRangeForMemberCardSessions();
    let cancelled = false;

    (async () => {
      try {
        const sessions = (await WorkoutService.getMemberWorkoutSessionsLight(
          memberId,
          fromStr,
          toStr,
          trainerId
        )) as SessionLike[];
        if (cancelled || loadGen !== metricsGenRef.current) return;
        setSelectedMetrics(
          computeMemberRosterMetrics(sessions ?? [], trainerId, joinDateYmd(relationship), {
            todayYmd,
          })
        );
      } catch {
        if (!cancelled && loadGen === metricsGenRef.current) {
          setSelectedMetrics(emptyMetrics());
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [memberId, trainerId, todayYmd, relationship]);

  useEffect(() => {
    const actualMemberId = relationship?.member?.id ?? relationship?.memberId ?? memberId;
    if (!actualMemberId) return;
    const { year, month } = currentCalendarMonth;
    const loaded = sessionsLoadedForMonthRef.current;
    if (loaded && loaded.year === year && loaded.month === month) {
      // Month already loaded — no fetch; release the Today pin after this commit.
      if (scrollDayAwaitSessionsRef.current && scrollDayIntoViewKeyRef.current) {
        scrollDayReleaseAfterLayoutRef.current = true;
        // Layout for this commit already ran; force one more pass via release on next equalize.
        finishPendingDayScroll();
      }
      return;
    }

    sessionsLoadedForMonthRef.current = { year, month };
    const { fromDate, toDate } = memberCalendarVisibleRange(year, month);
    let cancelled = false;
    WorkoutService.getMemberWorkoutSessionsLight(actualMemberId, fromDate, toDate, trainerId || undefined).then((sessions) => {
      if (cancelled) return;
      if (scrollDayAwaitSessionsRef.current && scrollDayIntoViewKeyRef.current) {
        scrollDayReleaseAfterLayoutRef.current = true;
      }
      setAllWorkoutSessions((prev) => mergeSessionsPreservingStampPending(sessions, prev));
    });
    return () => {
      cancelled = true;
    };
  }, [
    currentCalendarMonth.year,
    currentCalendarMonth.month,
    relationship?.member?.id,
    relationship?.memberId,
    memberId,
    trainerId,
    finishPendingDayScroll,
  ]);

  useEffect(() => {
    if (!assignOpen || !profile?.id) return;
    if (availablePlans.length > 0) return;
    WorkoutService.getWorkoutPlans().then(setAvailablePlans).catch(console.error);
  }, [assignOpen, profile?.id, availablePlans.length]);

  useEffect(() => {
    if (!libraryOpen || !profile?.id) return;
    setLibraryLoading(true);
    const cached = CacheService.get<WorkoutSummary[]>(TRAINER_WORKOUTS_CACHE_KEY);
    if (cached) {
      setLibraryWorkouts(cached);
      setLibraryLoading(false);
    }
    WorkoutService.getWorkoutsSummary()
      .then((workouts) => {
        const trainerWorkouts = workouts.filter((w) => w.createdBy === profile.id);
        setLibraryWorkouts(trainerWorkouts);
        CacheService.set(TRAINER_WORKOUTS_CACHE_KEY, trainerWorkouts, { ttl: TRAINER_WORKOUTS_TTL_MS });
      })
      .catch(console.error)
      .finally(() => setLibraryLoading(false));
  }, [libraryOpen, profile?.id, TRAINER_WORKOUTS_CACHE_KEY]);

  const planNameByAssignmentId = useMemo(() => {
    const map: Record<string, string> = {};
    assignedPlans.forEach((p) => {
      if (p.id) map[p.id] = p.workoutPlan?.name || 'Plan';
    });
    return map;
  }, [assignedPlans]);

  const planNameForSession = useCallback(
    (s: CalendarSession): string | null => {
      if (!s.usersWorkoutplanId) return null;
      return planNameByAssignmentId[s.usersWorkoutplanId] ?? null;
    },
    [planNameByAssignmentId]
  );

  const calendarSessionsForMonth = useMemo(() => {
    const { year, month } = currentCalendarMonth;
    const { fromDate: fromStr, toDate: toStr } = memberCalendarVisibleRange(year, month);
    return allWorkoutSessions.filter((s) => {
      const key = sessionDateKey(s);
      if (!key || key < fromStr || key > toStr) return false;
      if (planFilter === null) return true;
      if (planFilter === '__individual__') return !s.usersWorkoutplanId;
      return planNameForSession(s) === planFilter;
    });
  }, [allWorkoutSessions, currentCalendarMonth, planFilter, planNameForSession]);

  const calendarSessionsByDate = useMemo(() => {
    const map: Record<string, CalendarSession[]> = {};
    calendarSessionsForMonth.forEach((s) => {
      const key = sessionDateKey(s);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [calendarSessionsForMonth]);

  const monthDays = useMemo(
    () => buildMemberCalendarGrid(currentCalendarMonth.year, currentCalendarMonth.month),
    [currentCalendarMonth.year, currentCalendarMonth.month]
  );

  const weekRows = useMemo(() => {
    const rows: (typeof monthDays)[] = [];
    for (let w = 0; w < monthDays.length / 7; w++) {
      rows.push(monthDays.slice(w * 7, w * 7 + 7));
    }
    return rows;
  }, [monthDays]);

  // Equal-height day cells; optionally pin a day to the top after show more/less / Today
  useLayoutEffect(() => {
    const root = calWeeksRef.current;
    if (!root) return;
    const scrollEl = profileMainRef.current;
    const pendingDayKey = scrollDayIntoViewKeyRef.current;
    const savedMainScroll = scrollEl?.scrollTop ?? 0;

    const dayEls = root.querySelectorAll<HTMLElement>('.md-cal-day');
    dayEls.forEach((el) => {
      el.style.height = 'auto';
    });
    let maxH = 100;
    dayEls.forEach((el) => {
      maxH = Math.max(maxH, el.scrollHeight);
    });
    dayEls.forEach((el) => {
      el.style.height = `${maxH}px`;
    });

    if (pendingDayKey) {
      scrollDayToTopOfMain(pendingDayKey);
      // When switching months via Today, keep the pin until sessions load and heights settle.
      if (!scrollDayAwaitSessionsRef.current || scrollDayReleaseAfterLayoutRef.current) {
        scrollDayIntoViewKeyRef.current = null;
        scrollDayAwaitSessionsRef.current = false;
        scrollDayReleaseAfterLayoutRef.current = false;
      }
      return;
    }

    if (scrollEl) scrollEl.scrollTop = savedMainScroll;
  }, [
    weekRows,
    calendarSessionsByDate,
    planFilter,
    currentCalendarMonth,
    stamp,
    visibleCardLimit,
    scrollDayToTopOfMain,
  ]);

  // Reset overflow expansion when changing month
  useEffect(() => {
    setVisibleCardLimit(MAX_VISIBLE_CARDS);
  }, [currentCalendarMonth.year, currentCalendarMonth.month]);

  const planFilterOptions = useMemo(() => {
    const set = new Set<string>();
    assignedPlans.forEach((p) => {
      const name = p.workoutPlan?.name?.trim();
      if (name) set.add(name);
    });
    let hasIndividual = false;
    allWorkoutSessions.forEach((s) => {
      const name = planNameForSession(s);
      if (name) {
        set.add(name);
        return;
      }
      if (!s.usersWorkoutplanId) hasIndividual = true;
    });
    return {
      plans: Array.from(set).sort((a, b) => a.localeCompare(b)),
      hasIndividual,
    };
  }, [assignedPlans, allWorkoutSessions, planNameForSession]);

  useEffect(() => {
    if (planFilter === null || planFilter === '__individual__') return;
    if (!planFilterOptions.plans.includes(planFilter)) {
      setPlanFilter(null);
    }
  }, [planFilter, planFilterOptions.plans]);

  const planFilterLabel =
    planFilter === '__individual__' ? 'Individual workouts' : planFilter || 'All plans';

  const jumpCalendarToPlanStart = useCallback(
    (planName: string): boolean => {
      const matches = assignedPlans.filter(
        (p) => p.workoutPlan?.name?.trim() === planName && !!p.startDate
      );
      let ymd: string | null = null;
      if (matches.length > 0) {
        matches.sort((a, b) => {
          const rank = (s: string) => (s === 'active' || s === 'paused' ? 0 : 1);
          const byStatus = rank(a.status) - rank(b.status);
          if (byStatus !== 0) return byStatus;
          return String(a.startDate).localeCompare(String(b.startDate));
        });
        ymd = String(matches[0].startDate).split('T')[0];
      } else {
        const sessionKeys = allWorkoutSessions
          .filter((s) => planNameForSession(s) === planName)
          .map(sessionDateKey)
          .filter(Boolean)
          .sort();
        ymd = sessionKeys[0] ?? null;
      }
      if (!ymd) return false;
      const [y, m] = ymd.split('-').map(Number);
      if (!y || !m) return false;
      setCurrentCalendarMonth({ year: y, month: m - 1 });
      return true;
    },
    [assignedPlans, allWorkoutSessions, planNameForSession]
  );

  const pendingPlanJumpRef = useRef<string | null>(null);
  useEffect(() => {
    const pending = pendingPlanJumpRef.current;
    if (!pending) return;
    if (pending !== planFilter) {
      pendingPlanJumpRef.current = null;
      return;
    }
    if (jumpCalendarToPlanStart(pending)) {
      pendingPlanJumpRef.current = null;
    }
  }, [assignedPlans, allWorkoutSessions, planFilter, jumpCalendarToPlanStart]);

  const selectPlanFilter = useCallback(
    (value: PlanFilterValue) => {
      setPlanFilter(value);
      setPlanFilterOpen(false);
      if (value && value !== '__individual__') {
        pendingPlanJumpRef.current = value;
        if (jumpCalendarToPlanStart(value)) {
          pendingPlanJumpRef.current = null;
        }
      } else {
        pendingPlanJumpRef.current = null;
      }
    },
    [jumpCalendarToPlanStart]
  );

  const exitStampMode = useCallback(
    (opts?: { discardPending?: boolean }) => {
      const current = stampRef.current;
      if (current?.saving) return;
      if (opts?.discardPending) {
        setAllWorkoutSessions((prev) => prev.filter((s) => !isStampPendingSessionId(s.id)));
      }
      setStamp(null);
    },
    []
  );

  const commitStampPastes = useCallback(async () => {
    const current = stampRef.current;
    const actualMemberId = relationship?.member?.id || relationship?.memberId || memberId;
    if (!current || current.saving) return;

    if (current.pendingDates.length === 0) {
      setStamp(null);
      return;
    }

    if (!actualMemberId) {
      showToast('Could not paste', 'Member not loaded.');
      return;
    }

    const dates = [...current.pendingDates];
    const workoutName = current.name;
    setStamp((prev) => (prev ? { ...prev, saving: true } : prev));

    try {
      await Promise.all(
        dates.map((ymd) =>
          WorkoutService.assignWorkoutToMember(actualMemberId, current.workoutId, ymd)
        )
      );
      // Drop optimistic rows before refresh so server sessions don't double up.
      setAllWorkoutSessions((prev) => prev.filter((s) => !isStampPendingSessionId(s.id)));
      await refreshPlansAndSessionsForVisibleMonth();
      setStamp(null);
      showToast(
        'Workouts pasted',
        `${dates.length} day${dates.length === 1 ? '' : 's'} updated with ${workoutName}.`
      );
    } catch (err: unknown) {
      setAllWorkoutSessions((prev) => prev.filter((s) => !isStampPendingSessionId(s.id)));
      await refreshPlansAndSessionsForVisibleMonth();
      setStamp(null);
      showToast(
        'Some pastes failed',
        messageFromUnknownError(err) || 'Calendar refreshed — check which days were saved.'
      );
    }
  }, [
    relationship?.member?.id,
    relationship?.memberId,
    memberId,
    refreshPlansAndSessionsForVisibleMonth,
    showToast,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (stamp) {
        exitStampMode({ discardPending: true });
        return;
      }
      if (libraryOpen) {
        if (!libraryAssigningId) setLibraryOpen(false);
        return;
      }
      if (assignOpen) {
        if (!isAssigning) setAssignOpen(false);
        return;
      }
      if (drawerOpen) {
        setDrawerOpen(false);
        return;
      }
      if (visibleCardLimit > MAX_VISIBLE_CARDS) {
        setVisibleCardLimit(MAX_VISIBLE_CARDS);
        return;
      }
      if (planFilterOpen) setPlanFilterOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [assignOpen, drawerOpen, visibleCardLimit, planFilterOpen, libraryOpen, stamp, exitStampMode, isAssigning, libraryAssigningId]);

  const filteredPlans = useMemo(() => {
    const q = assignPlanSearch.trim().toLowerCase();
    if (!q) return availablePlans;
    return availablePlans.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [availablePlans, assignPlanSearch]);

  useEffect(() => {
    if (!planFilterOpen) return;
    const onScroll = () => {
      setPlanFilterOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [planFilterOpen]);

  useEffect(() => {
    if (!planFilterOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (planFilterBtnRef.current?.contains(t)) return;
      const menu = document.getElementById('md-plan-filter-menu');
      if (menu?.contains(t)) return;
      setPlanFilterOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [planFilterOpen]);

  const openPlanFilter = () => {
    if (planFilterOpen) {
      setPlanFilterOpen(false);
      return;
    }
    const rect = planFilterBtnRef.current?.getBoundingClientRect();
    if (rect) {
      setPlanFilterPos({ left: rect.left, top: rect.bottom + 6 });
    }
    setPlanFilterOpen(true);
  };

  const openDrawer = (session: CalendarSession) => {
    if (stamp) return;
    setDrawerSession(session);
    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const openAssignModal = () => {
    const today = toLocalDateKey(new Date());
    setAssignPlanDate(today);
    setSelectedPlanId('');
    setAssignPlanSearch('');
    setAssignError(null);
    setIsAssigning(false);
    setAssignOpen(true);
  };

  const closeAssignPanel = () => {
    if (isAssigning) return;
    setAssignOpen(false);
  };

  const openLibraryForDay = (ymd: string) => {
    if (stamp) return;
    setLibraryDate(ymd);
    setLibraryOpen(true);
  };

  const closeLibrary = () => {
    if (libraryAssigningId) return;
    setLibraryOpen(false);
  };

  const handleAssignSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    if (!memberId) return;
    if (!selectedPlanId) {
      setAssignError('Please select a workout plan');
      return;
    }
    if (!assignPlanDate) {
      assignPlanDateRef.current?.focus();
      return;
    }
    try {
      setIsAssigning(true);
      setAssignError(null);
      await WorkoutService.assignPlanToMember({
        userId: memberId,
        workoutPlanId: selectedPlanId,
        startDate: assignPlanDate,
      });
      const [y, m] = assignPlanDate.split('-').map(Number);
      const targetMonth = { year: y, month: m - 1 };
      await refreshPlansAndSessionsForVisibleMonth(targetMonth);
      setAssignOpen(false);
      const planName = availablePlans.find((p) => p.id === selectedPlanId)?.name || 'Plan';
      showToast('Plan assigned', `${planName} is now on their calendar.`);
    } catch (err: unknown) {
      setAssignError(messageFromUnknownError(err) || 'Failed to assign workout plan');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleOpenWorkoutDetails = () => {
    if (!memberId || !drawerSession) return;
    const workoutId = sessionWorkoutId(drawerSession);
    if (!workoutId) return;
    const m = relationship?.member ?? null;
    navigate(`/trainer-dashboard/member/${memberId}/workout/${workoutId}`, {
      state: m
        ? { memberDisplayName: displayNameFromProfile(m) || m.email || 'Member' }
        : { memberDisplayName: 'Member' },
    });
  };

  const removeSession = useCallback(
    async (session: CalendarSession) => {
      const workoutId = sessionWorkoutId(session);
      const scheduledDate = sessionDateKey(session);
      const name = sessionTitle(session);
      if (!workoutId || !scheduledDate) return;

      undoSnapshotRef.current = { workoutId, scheduledDate, name };
      try {
        await WorkoutService.deleteWorkoutSession(session.id);
        if (drawerSession?.id === session.id) {
          closeDrawer();
          setDrawerSession(null);
        }
        await refreshPlansAndSessionsForVisibleMonth();
        const actualMemberId = relationship?.member?.id || relationship?.memberId || memberId;
        showToast(
          'Workout removed',
          'Taken off the calendar.',
          () => {
            const snap = undoSnapshotRef.current;
            if (!snap || !actualMemberId) return;
            void (async () => {
              try {
                await WorkoutService.assignWorkoutToMember(
                  actualMemberId,
                  snap.workoutId,
                  snap.scheduledDate
                );
                await refreshPlansAndSessionsForVisibleMonth();
                undoSnapshotRef.current = null;
                setToast(null);
                showToast('Workout restored', `${snap.name} is back on the calendar.`);
              } catch (err: unknown) {
                showToast('Could not undo', messageFromUnknownError(err) || 'Please re-add from the library.');
              }
            })();
          },
          UNDO_TOAST_MS
        );
      } catch (err: unknown) {
        undoSnapshotRef.current = null;
        showToast('Could not remove', messageFromUnknownError(err) || 'Failed to remove workout');
      }
    },
    [
      drawerSession?.id,
      refreshPlansAndSessionsForVisibleMonth,
      relationship?.member?.id,
      relationship?.memberId,
      memberId,
      showToast,
    ]
  );

  const handleRemoveSession = () => {
    if (!drawerSession) return;
    void removeSession(drawerSession);
  };

  const enterStampMode = (s: CalendarSession) => {
    const workoutId = sessionWorkoutId(s);
    if (!workoutId) return;
    const sourceYmd = sessionDateKey(s);
    if (!sourceYmd) return;
    setVisibleCardLimit(MAX_VISIBLE_CARDS);
    closeDrawer();
    setStamp({
      workoutId,
      name: sessionTitle(s),
      sourceYmd,
      pasteCount: 0,
      pendingDates: [],
      estimatedDurationMinutes: sessionDurationMin(s) || undefined,
      exerciseCount: sessionExerciseCount(s),
      saving: false,
    });
  };

  const pasteStampOnDay = (ymd: string) => {
    const current = stampRef.current;
    const actualMemberId = relationship?.member?.id || relationship?.memberId || memberId;
    if (!current || current.saving || !actualMemberId) return;
    if (ymd === current.sourceYmd) return;

    const now = new Date().toISOString();
    const optimistic: CalendarSession = {
      id: `stamp-pending-${ymd}-${current.workoutId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      userId: actualMemberId,
      workoutId: current.workoutId,
      scheduledDate: ymd,
      status: 'scheduled',
      createdAt: now,
      updatedAt: now,
      usersWorkoutplanId: null,
      workout: {
        id: current.workoutId,
        name: current.name,
        estimatedDurationMinutes: current.estimatedDurationMinutes,
      },
    };

    setAllWorkoutSessions((prev) => [...prev, optimistic]);
    setStamp((prev) =>
      prev
        ? {
            ...prev,
            pasteCount: prev.pasteCount + 1,
            pendingDates: [...prev.pendingDates, ymd],
          }
        : prev
    );
  };

  const assignLibraryWorkout = async (workout: WorkoutSummary) => {
    const actualMemberId = relationship?.member?.id || relationship?.memberId || memberId;
    if (!actualMemberId || !libraryDate) return;
    setLibraryAssigningId(workout.id);
    try {
      await WorkoutService.assignWorkoutToMember(actualMemberId, workout.id, libraryDate);
      // Stay on the month currently being viewed (spill days from adjacent months included).
      await refreshPlansAndSessionsForVisibleMonth();
      setLibraryOpen(false);
      showToast('Workout added', `${workout.name} is on their calendar.`);
    } catch (err: unknown) {
      showToast('Could not assign', messageFromUnknownError(err) || 'Failed to assign workout');
    } finally {
      setLibraryAssigningId(null);
    }
  };

  const handleCreateNewWorkout = () => {
    if (!memberId || !libraryDate) return;
    navigate('/trainer-dashboard/workout/new', {
      state: {
        returnTo: `/trainer-dashboard/member/${memberId}`,
        assignOnCreate: {
          memberId,
          date: libraryDate,
        },
      },
    });
  };

  const handleCreateNewPlan = () => {
    if (!memberId) return;
    setAssignOpen(false);
    navigate('/trainer-dashboard/workout-plan/new', {
      state: {
        returnTo: `/trainer-dashboard/member/${memberId}`,
      },
    });
  };

  const goToMembersList = () => {
    persistTrainerDashboardSection('members');
    navigate('/trainer-dashboard');
  };

  const member = relationship?.member ?? null;
  const memberHeaderName = useMemo(
    () => displayNameFromProfile(member) || member?.email || 'Unknown',
    [member]
  );
  const memberHeaderEmail = member?.email || '';
  const memberHeaderInitials = useMemo(
    () => getInitials(displayNameFromProfile(member) || member?.email || 'M'),
    [member]
  );

  const mainErrorMessage = error;

  const calendarMonthLabel = `${MONTH_NAMES[currentCalendarMonth.month]} ${currentCalendarMonth.year}`;
  const today = new Date();
  const inStampMode = !!stamp;

  const renderSessionCard = (s: CalendarSession) => {
    const badge = mapSessionStatusToDesignBadge(s.status);
    const duration = sessionDurationMin(s);
    const exCount = sessionExerciseCount(s);
    return (
      <div
        key={s.id}
        className={`md-cal-card${inStampMode ? ' stamp-quiet' : ''}`}
        role="button"
        tabIndex={inStampMode ? -1 : 0}
        onClick={() => openDrawer(s)}
        onKeyDown={(e) => {
          if (inStampMode) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openDrawer(s);
          }
        }}
      >
        {!inStampMode && (
          <div className="md-cal-card-actions">
            <button
              type="button"
              className="md-cal-card-action"
              aria-label="Copy workout"
              data-hint="Copy"
              onClick={(e) => {
                e.stopPropagation();
                enterStampMode(s);
              }}
            >
              <Copy size={13} strokeWidth={2} />
            </button>
            <button
              type="button"
              className="md-cal-card-action danger"
              aria-label="Delete workout"
              data-hint="Delete"
              onClick={(e) => {
                e.stopPropagation();
                void removeSession(s);
              }}
            >
              <Trash2 size={13} strokeWidth={2} />
            </button>
          </div>
        )}
        <div className="md-cal-card-title">{sessionTitle(s)}</div>
        <div
          className="md-cal-card-meta tnum"
          title={formatSessionCardMeta(exCount, duration)}
        >
          {formatSessionCardMetaCompact(exCount, duration)}
        </div>
        <span className={`md-cal-badge ${badge}`}>{designBadgeLabel(badge)}</span>
      </div>
    );
  };

  const drawerBadge = drawerSession ? mapSessionStatusToDesignBadge(drawerSession.status) : 'scheduled';
  const drawerPlanLabel = drawerSession
    ? planNameForSession(drawerSession) || 'Individual workout'
    : '';
  const drawerExerciseCount = drawerSession ? sessionExerciseCount(drawerSession) : null;

  const overlays =
    typeof document !== 'undefined'
      ? createPortal(
          <div className={`member-detail-overlays${inStampMode ? ' stamp-mode' : ''}`}>
            {planFilterOpen && planFilterPos && (
              <div
                className="md-plan-filter-menu"
                id="md-plan-filter-menu"
                style={{ left: planFilterPos.left, top: planFilterPos.top }}
                role="listbox"
                aria-label="Filter by plan"
              >
                <button
                  type="button"
                  className={`md-plan-filter-option${planFilter === null ? ' selected' : ''}`}
                  onClick={() => selectPlanFilter(null)}
                >
                  <span>All plans</span>
                  <Check className="check" width={14} height={14} aria-hidden />
                </button>
                {(planFilterOptions.plans.length > 0 || planFilterOptions.hasIndividual) && (
                  <div className="md-plan-filter-divider" />
                )}
                {planFilterOptions.plans.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`md-plan-filter-option${planFilter === p ? ' selected' : ''}`}
                    onClick={() => selectPlanFilter(p)}
                  >
                    <span>{p}</span>
                    <Check className="check" width={14} height={14} aria-hidden />
                  </button>
                ))}
                {planFilterOptions.hasIndividual && (
                  <>
                    {planFilterOptions.plans.length > 0 && (
                      <div className="md-plan-filter-divider" />
                    )}
                    <button
                      type="button"
                      className={`md-plan-filter-option${planFilter === '__individual__' ? ' selected' : ''}`}
                      onClick={() => selectPlanFilter('__individual__')}
                    >
                      <span>Individual workouts</span>
                      <Check className="check" width={14} height={14} aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )}

            {drawerSession && (
              <>
                {drawerOpen && <div className="md-scrim" onClick={closeDrawer} aria-hidden />}
                <aside
                  className={`md-drawer${drawerOpen ? ' open' : ''}`}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Workout details"
                >
                  <div className="md-drawer-header">
                    <div>
                      <div className="md-drawer-eyebrow">
                        <span className="dot" />
                        Workout details
                      </div>
                      <div className="md-drawer-title">{sessionTitle(drawerSession)}</div>
                    </div>
                    <button
                      type="button"
                      className="md-drawer-close"
                      aria-label="Close"
                      onClick={closeDrawer}
                    >
                      <X width={15} height={15} aria-hidden />
                    </button>
                  </div>
                  <div className="md-drawer-body">
                    <div className="md-drawer-row">
                      <span className="k">Date</span>
                      <span className="v tnum">{formatDateLong(sessionDateKey(drawerSession))}</span>
                    </div>
                    <div className="md-drawer-row">
                      <span className="k">Exercises</span>
                      <span className="v tnum">
                        {drawerExerciseCount != null
                          ? `${drawerExerciseCount} exercise${drawerExerciseCount === 1 ? '' : 's'}`
                          : '—'}
                      </span>
                    </div>
                    <div className="md-drawer-row">
                      <span className="k">Duration</span>
                      <span className="v tnum">
                        {sessionDurationMin(drawerSession) > 0
                          ? `${sessionDurationMin(drawerSession)} min`
                          : '—'}
                      </span>
                    </div>
                    <div className="md-drawer-row">
                      <span className="k">Status</span>
                      <span className="v">
                        <span className={`md-cal-badge ${drawerBadge}`}>
                          {designBadgeLabel(drawerBadge)}
                        </span>
                      </span>
                    </div>
                    <div className="md-drawer-row">
                      <span className="k">Plan</span>
                      <span className="v">{drawerPlanLabel}</span>
                    </div>
                  </div>
                  <div className="md-drawer-footer">
                    <button
                      type="button"
                      className="md-btn-primary"
                      style={{ flex: 1, justifyContent: 'center' }}
                      onClick={handleOpenWorkoutDetails}
                    >
                      Workout details
                    </button>
                    <button type="button" className="md-btn-danger" onClick={handleRemoveSession}>
                      Remove
                    </button>
                  </div>
                </aside>
              </>
            )}

            {libraryOpen && libraryDate ? (
              <TrainerWorkoutLibraryDrawer
                open
                contextLine={<>Adding to {formatShortCalendarDate(libraryDate)}</>}
                workouts={libraryWorkouts}
                loading={libraryLoading}
                busy={!!libraryAssigningId}
                onClose={closeLibrary}
                onCreateWorkout={handleCreateNewWorkout}
                onAssign={(workout) => {
                  void assignLibraryWorkout(workout);
                }}
              />
            ) : null}

            {assignOpen && (
              <>
                <div className="md-scrim" onClick={closeAssignPanel} aria-hidden />
                <aside
                  className="md-drawer open md-lib-drawer md-assign-drawer"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="md-assign-title"
                >
                  <div className="md-drawer-header md-lib-header">
                    <div className="md-lib-header-top">
                      <div>
                        <div className="md-drawer-eyebrow">
                          <span className="dot" />
                          From your library
                        </div>
                        <div className="md-drawer-title" id="md-assign-title">
                          Assign a plan
                        </div>
                      </div>
                      <button
                        type="button"
                        className="tr-dialog__close md-assign-close"
                        aria-label="Close"
                        onClick={closeAssignPanel}
                        disabled={isAssigning}
                      >
                        <X width={16} height={16} aria-hidden />
                      </button>
                    </div>
                    <div className="md-assign-date-row">
                      <label className="md-field-label" htmlFor="md-assign-plan-date">
                        Start date
                      </label>
                      <input
                        ref={assignPlanDateRef}
                        id="md-assign-plan-date"
                        type="date"
                        className="md-assign-date-input"
                        value={assignPlanDate}
                        onChange={(e) => setAssignPlanDate(e.target.value)}
                        onFocus={(e) => openNativeDatePicker(e.currentTarget)}
                        onClick={(e) => {
                          e.stopPropagation();
                          openNativeDatePicker(e.currentTarget);
                        }}
                        disabled={isAssigning}
                      />
                    </div>
                    <div className="md-assign-header-divider" aria-hidden />
                    <div className="md-lib-search">
                      <Search width={16} height={16} aria-hidden />
                      <input
                        type="search"
                        placeholder="Search plans…"
                        value={assignPlanSearch}
                        onChange={(e) => setAssignPlanSearch(e.target.value)}
                        autoFocus
                        disabled={isAssigning}
                      />
                    </div>
                  </div>
                  <div className="md-drawer-body md-lib-body">
                    <div className="md-lib-count">
                      {filteredPlans.length} plan{filteredPlans.length === 1 ? '' : 's'}
                    </div>
                    {assignError && <p className="md-assign-error">{assignError}</p>}
                    {availablePlans.length === 0 ? (
                      <div className="md-lib-empty">
                        <strong>No workout plans in your library yet.</strong>
                        <br />
                        Create a plan to assign it here.
                      </div>
                    ) : filteredPlans.length === 0 ? (
                      <div className="md-lib-empty">
                        <strong>No matches.</strong>
                        <br />
                        Try a different search.
                      </div>
                    ) : (
                      filteredPlans.map((p) => {
                        const perWeek = estimateSessionsPerWeek(p);
                        const selected = selectedPlanId === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            className={`md-lib-item md-assign-plan-item${selected ? ' selected' : ''}`}
                            disabled={isAssigning}
                            onClick={() => {
                              setSelectedPlanId(p.id);
                              setAssignError(null);
                            }}
                          >
                            <div className="md-lib-item-name">{p.name}</div>
                            {perWeek != null ? (
                              <div className="md-lib-item-meta tnum">
                                <span>{perWeek} sessions/wk</span>
                              </div>
                            ) : null}
                          </button>
                        );
                      })
                    )}
                  </div>
                  <div className="md-drawer-footer md-lib-footer md-assign-footer">
                    <button
                      type="button"
                      className="md-lib-create"
                      onClick={handleCreateNewPlan}
                      disabled={isAssigning}
                    >
                      <Plus width={15} height={15} aria-hidden />
                      Create a plan
                    </button>
                    <button
                      type="button"
                      className="md-btn-primary"
                      disabled={isAssigning || !selectedPlanId || !assignPlanDate || availablePlans.length === 0}
                      onClick={(e) => void handleAssignSubmit(e)}
                    >
                      {isAssigning ? 'Assigning…' : 'Assign'}
                    </button>
                  </div>
                </aside>
              </>
            )}

            {stamp && (
              <div className="md-stamp-bar open" role="status">
                <span className="md-stamp-bar-text">
                  Copying <strong>{stamp.name}</strong> — click a day to paste
                </span>
                <span className="md-stamp-count tnum">Pasted {stamp.pasteCount}</span>
                <button
                  type="button"
                  className="md-btn-primary"
                  disabled={!!stamp.saving}
                  onClick={() => void commitStampPastes()}
                >
                  {stamp.saving ? 'Saving…' : 'Done'}
                </button>
              </div>
            )}

            {toast && (
              <div className={`md-toast open${toast.undo ? ' has-undo' : ''}`} role="status">
                <div className="md-toast-check">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div className="md-toast-text">
                  <strong>{toast.title}</strong>
                  <span>{toast.sub}</span>
                </div>
                {toast.undo && (
                  <button type="button" className="md-toast-undo" onClick={toast.undo}>
                    Undo
                  </button>
                )}
              </div>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <TrainerAppLayout
      activeSection="members"
      onSectionSelect={createTrainerDrillDownSectionSelect(navigate)}
      shellClassName="bg-white"
      mainClassName="flex-1 flex flex-row overflow-hidden min-w-0"
      mainStyle={{ background: 'var(--bg-page)' }}
    >
      <div className={`member-detail${inStampMode ? ' stamp-mode' : ''}`}>
        <main className="md-profile-main" ref={profileMainRef}>
          {loading && !relationship ? (
            <div className="md-loading">
              <LoadingSpinner />
            </div>
          ) : mainErrorMessage && !relationship ? (
            <div className="md-error" role="alert">
              {mainErrorMessage}
            </div>
          ) : (
            <>
              <nav className="md-breadcrumb" aria-label="Breadcrumb">
                <button type="button" className="md-breadcrumb-link" onClick={goToMembersList}>
                  Members
                </button>
                <span className="md-breadcrumb-sep" aria-hidden>
                  /
                </span>
                <span className="md-breadcrumb-current" aria-current="page">
                  {memberHeaderName}
                </span>
              </nav>

              <header className="md-profile-head">
                <div className="md-profile-id">
                  <div className="md-avatar-lg">{memberHeaderInitials}</div>
                  <div>
                    <h1 className="md-profile-name">{memberHeaderName}</h1>
                    <div className="md-profile-email">{memberHeaderEmail}</div>
                  </div>
                </div>

                <div className="md-profile-stats">
                  <div className="md-stat" title={CADENCE_TOOLTIP}>
                    <span className="md-stat-label">Cadence</span>
                    <span className="md-stat-value tnum">
                      {formatCadenceDisplay(selectedMetrics.cadence)}
                      <small>×/wk</small>
                    </span>
                  </div>
                  <div className="md-stat">
                    <span className="md-stat-label">Last 8 weeks</span>
                    <div className="md-strip" title={STRIP_TITLE} aria-label="8-week consistency">
                      {selectedMetrics.strip.map((w, i) => (
                        <span key={`${w.weekStart}-${i}`} className={`bar ${w.bar}`} />
                      ))}
                    </div>
                  </div>
                </div>

                <button type="button" className="md-btn-primary" onClick={openAssignModal}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Assign plan
                </button>
              </header>

              <section className="md-calendar-section" id="workout-calendar">
                <div className="md-calendar-toolbar">
                  <h2>Workout Calendar</h2>
                  <div className="md-calendar-nav">
                    <div className="md-plan-filter">
                      <button
                        ref={planFilterBtnRef}
                        type="button"
                        className={`md-plan-filter-btn${planFilter ? ' filtered' : ''}${planFilterOpen ? ' menu-open' : ''}`}
                        onClick={openPlanFilter}
                        aria-haspopup="listbox"
                        aria-expanded={planFilterOpen}
                      >
                        <span>{planFilterLabel}</span>
                        <svg
                          className="chev"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    </div>
                    <div className="md-toolbar-divider" />
                    <button
                      type="button"
                      className="md-icon-btn"
                      aria-label="Previous month"
                      onClick={() =>
                        setCurrentCalendarMonth((m) =>
                          m.month === 0
                            ? { year: m.year - 1, month: 11 }
                            : { year: m.year, month: m.month - 1 }
                        )
                      }
                    >
                      <ChevronLeftIcon />
                    </button>
                    <div className="md-calendar-label">
                      <Calendar width={14} height={14} aria-hidden />
                      <span className="tnum">{calendarMonthLabel}</span>
                    </div>
                    <button
                      type="button"
                      className="md-icon-btn"
                      aria-label="Next month"
                      onClick={() =>
                        setCurrentCalendarMonth((m) =>
                          m.month === 11
                            ? { year: m.year + 1, month: 0 }
                            : { year: m.year, month: m.month + 1 }
                        )
                      }
                    >
                      <ChevronRightIcon />
                    </button>
                    <button
                      type="button"
                      className="md-btn-ghost"
                      onClick={() => {
                        const d = new Date();
                        const todayKey = toLocalDateKey(d);
                        const monthChanged =
                          currentCalendarMonth.year !== d.getFullYear() ||
                          currentCalendarMonth.month !== d.getMonth();
                        scrollDayIntoViewKeyRef.current = todayKey;
                        scrollDayAwaitSessionsRef.current = monthChanged;
                        setCurrentCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
                      }}
                    >
                      Today
                    </button>
                  </div>
                </div>

                <div className="md-calendar-grid">
                  <div className="md-cal-weekdays">
                    <div>Mon</div>
                    <div>Tue</div>
                    <div>Wed</div>
                    <div>Thu</div>
                    <div>Fri</div>
                    <div>Sat</div>
                    <div>Sun</div>
                  </div>
                  <div className="md-cal-weeks" ref={calWeeksRef}>
                    {weekRows.map((row, wi) => (
                      <div className="md-cal-week" key={`w-${wi}`}>
                        {row.map((cell) => {
                          const key = cell.ymd;
                          const isToday =
                            cell.year === today.getFullYear() &&
                            cell.month === today.getMonth() &&
                            cell.day === today.getDate();
                          const entries = calendarSessionsByDate[key] || [];
                          const visible = entries.slice(0, visibleCardLimit);
                          const overflow = Math.max(0, entries.length - visibleCardLimit);
                          const isEmptyDay = entries.length === 0;
                          const isExpandedGrid = visibleCardLimit > MAX_VISIBLE_CARDS;
                          const isStampSource = inStampMode && stamp?.sourceYmd === key;
                          const isOutside = !cell.inCurrentMonth;
                          return (
                            <div
                              className={`md-cal-day${isToday ? ' is-today' : ''}${isEmptyDay ? ' empty-day' : ''}${isOutside ? ' is-outside' : ''}${isExpandedGrid ? ' is-expanded' : ''}${inStampMode ? ' stamp-target' : ''}${isStampSource ? ' md-cal-day--source' : ''}`}
                              key={key}
                              data-day-key={key}
                              onClick={
                                inStampMode && !isStampSource
                                  ? () => {
                                      pasteStampOnDay(key);
                                    }
                                  : undefined
                              }
                              role={inStampMode && !isStampSource ? 'button' : undefined}
                              tabIndex={inStampMode && !isStampSource ? 0 : undefined}
                              onKeyDown={
                                inStampMode && !isStampSource
                                  ? (e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        pasteStampOnDay(key);
                                      }
                                    }
                                  : undefined
                              }
                            >
                              <div className="md-cal-date-row">
                                <span className="md-cal-date tnum">{cell.day}</span>
                                {isToday && <span className="md-cal-today-pill">Today</span>}
                              </div>
                              {isEmptyDay && <div className="md-cal-rest">Rest day</div>}
                              {visible.map((s) => renderSessionCard(s))}
                              {overflow > 0 && (
                                <button
                                  type="button"
                                  className="md-cal-more"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (inStampMode) return;
                                    scrollDayIntoViewKeyRef.current = key;
                                    setVisibleCardLimit((prev) => Math.max(prev, entries.length));
                                  }}
                                >
                                  +{overflow} more
                                </button>
                              )}
                              {isExpandedGrid && overflow === 0 && entries.length > MAX_VISIBLE_CARDS && (
                                <button
                                  type="button"
                                  className="md-cal-more"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    scrollDayIntoViewKeyRef.current = key;
                                    setVisibleCardLimit(MAX_VISIBLE_CARDS);
                                  }}
                                >
                                  Show less
                                </button>
                              )}
                              {!inStampMode && (
                                <button
                                  type="button"
                                  className="md-cal-add"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLibraryForDay(key);
                                  }}
                                >
                                  <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                  >
                                    <path d="M12 5v14M5 12h14" />
                                  </svg>
                                  Add workout
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </>
          )}
        </main>
      </div>
      {overlays}
    </TrainerAppLayout>
  );
};



function ChevronLeftIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}



